import React, { useState, useEffect } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Add from '@material-ui/icons/Add';

import PropTypes from 'prop-types';
import MediaUrlField from '../MediaUrlField/MediaUrlField';
import HeadlineField from '../HeadlineField/HeadlineField';
import DescriptionField from '../DescriptionField/DescriptionField';
import ThumbnailField from '../ImageUpload/ThumbnailField';
import BrandLogoField from '../ImageUpload/BrandLogoField';
import materialTheme from '../../reactMaterialTheme';
import Mixpanel from '../../shared/Mixpanel';

import { useCreativeInput } from '../../hooks';
import './SlideshowBuilder.scss';

const MAX_SLIDES_COUNT = 10;

const defaultSlide = () => ({ thumbnailUrl: '' });

const SlideshowBuilder = props => {
  const [creative, setCreative] = useState({
    slides: [defaultSlide(), defaultSlide()],
    slideshowAutoscroll: true,
    ...props.creative
  });

  const [behaviors, setBehaviors] = useState(creative.behaviors || {});

  const creativeInputProps = useCreativeInput(
    [
      'advertiser',
      'headline',
      'description',
      'name',
      'mediaUrl',
      'slideshowAutoscroll',
      'brandLogoUrl'
    ],
    creative,
    setCreative,
    props.CreativeService
  );

  useEffect(function setDefaultSlides() {
    if (!props.creative.id) {
      Mixpanel.track('New Carousel Demo');
    }
  }, []);

  useEffect(
    function syncChangesToCreativeService() {
      props.CreativeService.set('slides', creative.slides);
    },
    [creative.slides]
  );

  const handleThumbnailUrlChange = (url, slideIndex) => {
    let slides = creative.slides;
    slides[slideIndex].thumbnailUrl = url;
    setCreative({
      ...creative,
      slides
    });
  };

  const addSlide = () => {
    let slides = creative.slides;
    if (slides.length >= MAX_SLIDES_COUNT) {
      return;
    }
    slides.push(defaultSlide());
    setCreative({ ...creative, slides });
  };

  const deleteSlide = index => {
    let slides = creative.slides;
    slides.splice(index, 1);
    setCreative({ ...creative, slides });
  };

  const handleOgResponse = ogResponse => {
    const newCreative = { ...props.creative, ...ogResponse };
    setCreative(newCreative);
    props.CreativeService.set('headline', newCreative.headline);
    props.CreativeService.set('description', newCreative.description);
    props.CreativeService.set('thumbnailUrl', newCreative.thumbnailUrl);
  };

  const handleToggleCropBehavior = (event, value) => {
    const newValue = value !== undefined ? value : event.target.value;
    const newBehaviors = { ...behaviors, shouldContainThumbnail: newValue };

    setBehaviors(newBehaviors);
    props.CreativeService.setBehavior('shouldContainThumbnail', newValue);
  };

  return (
    <MuiThemeProvider theme={materialTheme}>
      <form className="p2 slideshow-builder">
        <div className="ad-builder">
          <div className="row">
            <TextField
              {...creativeInputProps.nameProps}
              label="Ad Name"
              className="col-xs-10"
              inputProps={{ 'data-testid': 'creative-name-input' }}
              required
            />
          </div>
          <div className="row mt2">
            <TextField
              {...creativeInputProps.advertiserProps}
              label={
                <label htmlFor={creativeInputProps.advertiserProps.id}>
                  Brand Name
                </label>
              }
              className="col-xs-10"
              onFocus={event => event.target.select()}
              required
            />
          </div>
          <div className="row mt2">
            <BrandLogoField
              logoUrl={creativeInputProps.brandLogoUrlProps.value}
              onLogoUrlChange={creativeInputProps.brandLogoUrlProps.onChange}
            />
          </div>
          <div className="row">
            <MediaUrlField
              mediaUrl={creativeInputProps.mediaUrlProps.value}
              onChange={creativeInputProps.mediaUrlProps.onChange}
              creative={props.creative}
              OpenGraphDataService={props.OpenGraphDataService}
              onOgResponse={handleOgResponse}
              CreativeService={props.CreativeService}
            />
          </div>
          <div className="row">
            Upload up to 10 thumbnail images. We recommend at least 2 images
            that are 1000x750px or larger.
          </div>
          <div className="row slideshow_thumbnail-row">
            {props.creative.slides &&
              props.creative.slides.map((slide, index) => {
                return (
                  <ThumbnailField
                    thumbnailUrl={slide.thumbnailUrl}
                    onChange={url => handleThumbnailUrlChange(url, index)}
                    showImageSpecs={false}
                    key={index}
                    removeable={index >= 2}
                    onRemove={() => deleteSlide(index)}
                  />
                );
              })}
            {props.creative.slides &&
              props.creative.slides.length < MAX_SLIDES_COUNT && (
                <button
                  onClick={addSlide}
                  type="button"
                  className="slideshow_add-slide-button"
                >
                  <Add
                    data-testid="add-slide-button"
                    style={{ color: '#cdcdcd' }}
                  />
                </button>
              )}
          </div>
          <div className="row">
            <HeadlineField
              headline={creativeInputProps.headlineProps.value}
              onChange={creativeInputProps.headlineProps.onChange}
            />
          </div>
          <div className="row">
            <DescriptionField
              description={creativeInputProps.descriptionProps.value}
              onChange={creativeInputProps.descriptionProps.onChange}
            />
          </div>
          <div className="row">
            <div>
              <FormGroup>
                <FormControlLabel
                  label="Auto-scroll content"
                  control={
                    <Checkbox
                      inputProps={{
                        'data-testid': 'autoscroll-checkbox'
                      }}
                      name="slideshowAutoscroll"
                      checked={
                        creativeInputProps.slideshowAutoscrollProps.value
                      }
                      onChange={
                        creativeInputProps.slideshowAutoscrollProps.onChange
                      }
                    />
                  }
                />
                <FormControlLabel
                  label="Do not crop thumbnails"
                  control={
                    <Checkbox
                      checked={behaviors.shouldContainThumbnail}
                      onChange={handleToggleCropBehavior}
                      inputProps={{
                        'data-testid': 'checkbox-behavior-no-crop'
                      }}
                    />
                  }
                />
              </FormGroup>
            </div>
          </div>
        </div>
      </form>
    </MuiThemeProvider>
  );
};

SlideshowBuilder.propTypes = {
  creative: PropTypes.object.isRequired,
  CreativeService: PropTypes.shape({
    set: PropTypes.func,
    setBehavior: PropTypes.func
  }).isRequired,
  OpenGraphDataService: PropTypes.object.isRequired,
  $modal: PropTypes.object.isRequired,
  $rootScope: PropTypes.object.isRequired
};

export default SlideshowBuilder;
